import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ThemeService } from '../service/theme.service';
import { ClientService } from '../service/client.service';
import { DashboardService } from '../service/dashboard.service';
import { CognitoService } from '../service/cognito.service';
import { InvoiceGenerationStateService } from '../service/invoice-generation-state.service';
import { environment } from '../environments/environment';
import { ActivatedRoute } from '@angular/router';

interface DeviceOption {
  id: string;
  name: string;
}

@Component({
  selector: 'app-invoice-generation',
  templateUrl: './invoice-generation.component.html',
  styleUrls: ['./invoice-generation.component.css', '../../global-elements.css'],
  standalone: false,
})
export class InvoiceGenerationComponent implements OnInit {
  // Client dropdown options.
  clients: any[] = [];
  // Current client selection.
  selectedClient = '';
  // Device options for the selected client.
  deviceOptions: DeviceOption[] = [];
  // Selected device IDs from the multiselect.
  selectedDevices: string[] = [];
  // Selected invoice type (subscription or installation).
  invoiceType: 'subscription' | 'installation' | '' = '';
  // Async loading state for device list.
  loadingDevices = false;
  // Toggle visibility of filter form.
  showFilters = true;

  constructor(
    public theme: ThemeService,
    private clientService: ClientService,
    private dashboardService: DashboardService,
    private cognitoService: CognitoService,
    private invoiceState: InvoiceGenerationStateService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    // Role check
    this.cognitoService.getCurrentRole(
      [environment.users.role.administrator],
      [environment.users.superAdmin],
    );
  }

  async ngOnInit(): Promise<void> {
    // Load client list on entry.
    await this.loadClients();
    // Seed selections from shared state first.
    if (this.invoiceState.clientId) {
      this.selectedClient = this.invoiceState.clientId;
      this.selectedDevices = [...this.invoiceState.devices];
      this.invoiceType = this.invoiceState.invoiceType;
    }

    // Preselect client if routed from client list (URL wins for client id).
    const routeClientId = this.route.snapshot.paramMap.get('clientId') || '';
    if (routeClientId) {
      this.selectedClient = routeClientId;
      if (routeClientId !== this.invoiceState.clientId) {
        this.selectedDevices = [];
      }
    }

    if (this.selectedClient) {
      await this.onClientChange(true);
    }
  }

  async loadClients(): Promise<void> {
    try {
      // Populate client list from service.
      await this.clientService.getClientsForSelect();
      this.clients = this.clientService.clientsArray || [];
    } catch (error) {
      console.error('[InvoiceGeneration] Failed to load clients', error);
      this.clients = [];
    }
  }

  async onClientChange(preserveSelection = false): Promise<void> {
    // Reset selections when client changes unless explicitly preserved.
    const previousSelection = preserveSelection ? [...this.selectedDevices] : [];
    if (!preserveSelection) {
      this.selectedDevices = [];
    }
    this.deviceOptions = [];

    if (!this.selectedClient) {
      return;
    }

    this.loadingDevices = true;
    try {
      // Fetch devices associated with the client.
      const thingNames = await this.dashboardService.getThingNamesByEntityId(
        this.selectedClient
      );
      this.deviceOptions = (thingNames || []).map((name: string) => ({
        id: name,
        name,
      }));
      if (preserveSelection && previousSelection.length) {
        const optionSet = new Set(this.deviceOptions.map((device) => device.id));
        this.selectedDevices = previousSelection.filter((id) => optionSet.has(id));
      }
    } catch (error) {
      console.error('[InvoiceGeneration] Failed to load devices', error);
      this.deviceOptions = [];
      if (!preserveSelection) {
        this.selectedDevices = [];
      }
    } finally {
      this.loadingDevices = false;
    }
  }

  selectAllDevices(): void {
    // Select all device IDs from current options.
    this.selectedDevices = this.deviceOptions.map((device) => device.id);
  }

  clearSelectedDevices(): void {
    // Clear selected device IDs.
    this.selectedDevices = [];
  }

  canProceed(): boolean {
    // Require all inputs and at least one valid device selection before enabling prepare.
    if (!this.selectedClient || !this.invoiceType || this.loadingDevices) {
      return false;
    }
    if (this.selectedDevices.length === 0) {
      return false;
    }
    const optionSet = new Set(this.deviceOptions.map((device) => device.id));
    return this.selectedDevices.every((id) => optionSet.has(id));
  }

  goNext(): void {
    if (!this.canProceed()) {
      return;
    }

    // Persist selections to state before routing.
    const target =
      this.invoiceType === 'subscription'
        ? ['/invoice-generate', this.selectedClient, 'subscription-invoice']
        : ['/invoice-generate', this.selectedClient, 'installation-invoice'];

    this.invoiceState.setState({
      clientId: this.selectedClient,
      devices: [...this.selectedDevices],
      invoiceType: this.invoiceType,
    });
    this.router.navigate(target);
    this.showFilters = false;
  }

  toggleFilters(): void {
    // Show/hide filter form without losing selections.
    this.showFilters = !this.showFilters;
  }
}
